/**
 * Turns an insight's data into the presentation the prototype specifies.
 *
 * Both mappings existed only as hardcoded markup before: the card always
 * printed the "Atenção" tag regardless of `severity`, and the prototype
 * carries the related area as `data-insight-link` written by hand on each
 * static card. Here they are derived from the fields the API already sends.
 */

/** `severity` → the chip variant the prototype uses. */
export const severityTone = (severity) => {
  if (severity === 'critical') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'success';
};

/**
 * `category` → the dashboard area the insight is about, which becomes the
 * prototype's "Abrir {área}" link.
 *
 * Só duas categorias têm área própria, e é de propósito (PBI 4518):
 *
 * - `executor` saiu. O executor virou funcionamento interno do produto; o
 *   usuário não navega por ele, então mandá-lo para `/dashboard/executors`
 *   contrariava a própria PBI. O sinal de capacidade que vinha daí continua
 *   existindo, agora contado como atraso de automação, na categoria
 *   `execution`.
 * - `package` saiu. O pacote deixou de ser entidade independente: hoje ele
 *   faz parte da automação, e o insight sobre ele aponta para a automação.
 * - `general` nunca teve área.
 *
 * Categoria sem entrada aqui devolve `null`, e o cartão simplesmente não
 * mostra o link — que é o comportamento certo para uma linha antiga de
 * executor ou pacote que tenha escapado da migration.
 *
 * `path` is a router path, NOT a hash fragment. The prototype writes
 * `#/dashboard/...` because it runs on hash routing; this app mounts a
 * `BrowserRouter` (`index.js`), where a `#` href only moves `location.hash`
 * and the router never sees it — the link would do nothing.
 *
 * Os dois caminhos apontam para o destino final, não para o redirecionamento:
 * `/dashboard/jobs` e `/dashboard/schedules` são `<Navigate>` para
 * `automations` (`App.js`), e passar por eles custa um salto à toa.
 */
const AREA_BY_CATEGORY = {
  execution: { key: 'execution', path: '/dashboard/automations' },
  schedule: { key: 'schedule', path: '/dashboard/automations?tab=schedules' },
};

export const insightAreaLink = (category) => AREA_BY_CATEGORY[category] || null;
